import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import modal from "antd/lib/modal";
import {
  dateCellRenderer,
  FeatureTemplateListActionsCellRenderer,
  featureTemplateListDefaultCellRenderer,
  featureTemplateListNameCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { GridLoadingIndicator } from "components/widgets";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateFilter } from "utils/dateutils";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { FeatureTypes } from "entity-app/constants";
import { COMPUTE_STRATEGY_LABELS, InfoMessages } from "../../constants";
import ComputeStrategyTag from "./widgets/compute-strategy-tag";

const getCreatedUser = (params: any) => {
  const user = params.data?.created_by_user;
  return user ? `${user.first_name} ${user.last_name}` : "";
};

const templateLogicCellRenderer = (params: any) => {
  return <ComputeStrategyTag label={params.value} size="middle" />;
};

export default function FeatureTemplateList(props: {
  setdetailsViewData: (data: any) => void;
  workflowTemplates: Array<any> | undefined;
  canEditFeatureDBTemplate: boolean;
  onChangeDefaultTemplate: (...args: any[]) => any;
  savingDefaultTemplate: boolean;
  onWorkflowCopy: (payload: any) => void;
  onToggleTemplateDisabled: (data: any) => void;
  featureId?: FeatureTypes;
}) {
  const {
    setdetailsViewData,
    workflowTemplates,
    canEditFeatureDBTemplate,
    onChangeDefaultTemplate,
    savingDefaultTemplate,
    onWorkflowCopy,
    onToggleTemplateDisabled,
    featureId
  } = props;

  const gridRef = useRef<AgGridReact>(null);

  const [isGridReady, setGridReady] = useState(false);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "TEMPLATE NAME",
        headerTooltip: "TEMPLATE NAME",
        field: "name",
        colId: "name",
        cellRenderer: featureTemplateListNameCellRenderer,
        minWidth: 350,
        filter: false,
        sort: "asc"
      },
      ...(featureId === FeatureTypes.MATERIALS
        ? [
            {
              headerName: "TEMPLATE LOGIC",
              headerTooltip: "TEMPLATE LOGIC",
              colId: "dateblock_compute_strategy",
              cellRenderer: templateLogicCellRenderer,
              valueGetter: (params: any) => {
                return COMPUTE_STRATEGY_LABELS[
                  params.data.dateblock_compute_strategy
                    ?.key as keyof typeof COMPUTE_STRATEGY_LABELS
                ].label;
              },
              filter: false
            }
          ]
        : []),
      {
        headerName: "CREATED BY",
        headerTooltip: "CREATED BY",
        colId: "created_by",
        comparator: DateFilter.comparator,
        valueGetter: getCreatedUser,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "CREATED ON",
        headerTooltip: "CREATED ON",
        colId: "created_at",
        field: "created_at",
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator
      },
      {
        headerName: "DEFAULT",
        headerTooltip: "DEFAULT",
        field: "default",
        colId: "default",
        width: 90,
        maxWidth: 110,
        cellRenderer: featureTemplateListDefaultCellRenderer,
        sortable: false
      },
      {
        headerName: "Actions",
        headerTooltip: "Actions",
        pinned: "right",
        cellRenderer: FeatureTemplateListActionsCellRenderer,
        minWidth: 250,
        sortable: false,
        filter: false
      }
    ];
  }, [featureId]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      autoHeight: true,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const onTemplateNameClick = (cellData: any) => {
    if (savingDefaultTemplate) return;
    setdetailsViewData({
      data: cellData,
      openInEditMode: false
    });
  };

  const onTemplateEditClick = (cellData: any) => {
    setdetailsViewData({
      data: cellData,
      openInEditMode: true
    });
  };

  const onTemplateDefaultChange = (cellData: any) => {
    modal.confirm({
      title: "Confirmation",
      content: InfoMessages.defaultTemplateChangeCOnfirmation,
      onOk() {
        onChangeDefaultTemplate(cellData);
      },
      okText: "Yes",
      cancelText: "Cancel"
    });
  };

  const onToggleTemplateDisabledConfirm = (
    cellData: any,
    newToggleState: boolean
  ) => {
    if (cellData.default) {
      modal.error({
        title: "Cannot Disable Default Template",
        content: "Set an another template as default to disable this template.",
        onOk() {},
        okText: "Ok"
      });
      return;
    }

    modal.confirm({
      title: "Confirmation",
      content: newToggleState
        ? InfoMessages.confirmEnableMDBTemplate
        : InfoMessages.confirmDisableMDBTemplate,
      onOk() {
        onToggleTemplateDisabled(cellData);
      },
      okText: "Yes",
      cancelText: "Cancel"
    });
  };

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef.current) {
        gridRef.current.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isGridReady]);

  useEffect(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.refreshCells({ force: true });
  }, [savingDefaultTemplate]);

  const getRowId = (params: any) => {
    return params.data.id;
  };

  return (
    <div>
      <div className="h-[500px] ag-theme-alpine pl-[25px] pr-[50px]">
        <AgGridReact<any>
          ref={gridRef}
          onFirstDataRendered={() => {
            setGridReady(true);
            gridRef?.current?.api?.sizeColumnsToFit();
          }}
          rowData={workflowTemplates}
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={false} // Optional - set to 'true' to have rows animate when sorted
          loadingOverlayComponent={GridLoadingIndicator}
          suppressDragLeaveHidesColumns
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          suppressContextMenu
          context={{
            onTemplateNameClick,
            onTemplateEditClick,
            onTemplateDefaultChange,
            onWorkflowCopy,
            onToggleTemplateDisabledConfirm,
            savingDefaultTemplate,
            canEditFeatureDBTemplate,
            featureTemplates: workflowTemplates
          }}
          getRowId={getRowId}
        />
      </div>
    </div>
  );
}
